import { DialogTitle } from "@material-ui/core";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { TextContent } from "js/Components/TextContent";
import BeautifulLogo from "js/react/components/BeautifulLogo";
import { DialogContent } from "js/react/components/Dialogs/BaseDialog";
import { Gap10 } from "js/react/components/Gap";
import ProgressBar from "js/react/components/ProgressBar";

import withElements from "../../../withElements";
import withStripe from "../../../withStripe";

import { AddressForm } from "../../components/AddressForm/AddressForm";
import { BillingIntervalMenu } from "../../components/BillingIntervalMenu/BillingIntervalMenu";
import { PaymentForm } from "../../components/PaymentForm/PaymentForm";
import { PlanSummary } from "../../components/PlanSummary/PlanSummary";
import { PromoCoupon, PromoForm } from "../../components/PromoForm/PromoForm";
import { SubmissionButtons } from "../../components/SubmissionButtons/SubmissionButtons";
import { TermsOfUseAndRefundPolicy } from "../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { LoadingState } from "../components/LoadingState";

import {
    BeautifulLogoContainer,
    Container,
    ContentContainer,
    FormContainer,
    HeaderContainer,
    ProgressBarContainer,
    ScrollContainer
} from "../../components/FormStyling";

import { type ProCheckoutState, ProCheckoutButtonsContainer, ProCheckoutContainer, ProCheckoutContentContainer } from "./ProCheckout";

export const ProCheckoutContent = withStripe(withElements(({
    isTrial,
    isAnnual,
    isUpgradeMode,
    isSubmitting,
    canSubmit,
    showProgressLoader,
    paymentMethodCreationError,
    billingAddress,
    billingInterval,
    productDisplayName,
    prices,
    promo,
    availableReferralCredit,
    handleBillingIntervalChange,
    handlePaymentElementChange,
    handleBillingAddressChange,
    handleVerifyPromoCode,
    handleSubmitPayment,
    handleCancel,
    stripe,
    elements
}: ProCheckoutState & { stripe: Stripe, elements: StripeElements }) => {
    const { t } = useTranslation();
    if (isTrial) {
        if (isSubmitting && showProgressLoader) {
            return (<LoadingState
                title={isTrial ? t("Starting free trial") : t("Upgrading plan")}
                subtitle={t("This will only take a moment...")}
            />);
        }

        return (<>
            <DialogTitle>{t("Start your 14 day free trial")}</DialogTitle>
            <DialogContent>
                <ProCheckoutContainer>
                    <ProCheckoutContentContainer>
                        <PlanSummary
                            isTrial={isTrial}
                            billingInterval={billingInterval}
                            productDisplayName={productDisplayName}
                            prices={prices}
                            promo={promo}
                            availableReferralCredit={availableReferralCredit}
                            handleBillingIntervalChange={handleBillingIntervalChange}
                            handleVerifyPromoCode={handleVerifyPromoCode}
                        />

                        <Gap10 />
                        <PaymentForm
                            paymentMethodCreationError={paymentMethodCreationError}
                            handlePaymentElementChange={handlePaymentElementChange}
                        />
                        <Gap10 />
                        <AddressForm initialAddress={billingAddress} onChange={handleBillingAddressChange} />
                        <Gap10 />
                        {!promo ? <PromoForm verifyPromoCode={handleVerifyPromoCode} /> : <PromoCoupon coupon={promo.coupon} />}
                    </ProCheckoutContentContainer>

                    <ProCheckoutButtonsContainer >
                        <SubmissionButtons
                            submitLabel={t("Start Free Trial")}
                            onSubmit={() => handleSubmitPayment(stripe, elements)}
                            disabled={!canSubmit}
                        />

                        <TextContent largeLabel>
                            <Trans
                                i18nKey="Your subscription will <bold>automatically renew {{interval}}</bold>."
                                values={{ interval: t(isAnnual ? "annually" : "monthly") }}
                                components={{ bold: <strong /> }}
                            />
                            {" "}
                            <TermsOfUseAndRefundPolicy />
                        </TextContent>
                    </ProCheckoutButtonsContainer>
                </ProCheckoutContainer>
            </DialogContent>
        </>);
    }

    return (<>
        <DialogTitle>
            <Trans
                i18nKey="Upgrade to Beautiful.ai <accent>{{productDisplayName}}</accent>."
                values={{ productDisplayName }}
                components={{ accent: <span color="#11a9e2" /> }}
            />
        </DialogTitle>
        <DialogContent>
            <ProCheckoutContainer>
                <ProCheckoutContentContainer>
                    <TextContent largeLabel margin="0 0 30px 0">
                        {t("Sorry, you are not eligible for a 14-day free trial because you have already had a trial in the past.")}
                    </TextContent>

                    <BillingIntervalMenu
                        isTrial={isTrial}
                        billingInterval={billingInterval}
                        prices={prices}
                        showBestValueBadge={true}
                        promo={promo}
                        availableReferralCredit={availableReferralCredit}
                        onChange={handleBillingIntervalChange}
                    />
                    <PaymentForm
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={handlePaymentElementChange}
                    />
                    <AddressForm initialAddress={billingAddress} onChange={handleBillingAddressChange} />
                    {!promo ? <PromoForm verifyPromoCode={handleVerifyPromoCode} /> : <PromoCoupon coupon={promo.coupon} />}
                </ProCheckoutContentContainer>

                <ProCheckoutButtonsContainer>
                    <SubmissionButtons
                        submitLabel={isUpgradeMode ? t("Upgrade Now") : t("Confirm")}
                        onSubmit={() => handleSubmitPayment(stripe, elements)}
                        onCancel={handleCancel}
                        disabled={!canSubmit}
                    />
                </ProCheckoutButtonsContainer>
            </ProCheckoutContainer>
        </DialogContent>
    </>);
}, { appearanceVariant: "A" }));

export const ProCheckoutContentWithContainer = ({ children }: { children: React.ReactNode }) => {
    return (
        <Container>
            <HeaderContainer>
                <BeautifulLogoContainer>
                    <BeautifulLogo forceMarketing width={132.855} />
                </BeautifulLogoContainer>
                <ProgressBarContainer>
                    <ProgressBar progress={100} prevProgress={80} />
                </ProgressBarContainer>
            </HeaderContainer>
            <ScrollContainer>
                <ContentContainer >
                    <FormContainer>
                        {children}
                    </FormContainer>
                </ContentContainer>
            </ScrollContainer>
        </Container>
    );
};
